import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const Tabs = ({
  tabs,
  defaultTab = '',
  variant = 'default', // 'default', 'pills', 'underline'
  onChange,
  tabName = 'tab',
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useState(defaultTab);

  const setDataToSearchParams = (params) => {
    setSearchParams(
      (prev) => {
        const nextParams = new URLSearchParams(prev);
        Object.entries(params).forEach(([key, value]) => {
          if (value) {
            nextParams.set(key, value);
          } else {
            nextParams.delete(key);
          }
        });
        return nextParams;
      },
      { replace: true },
    );
  };

  useEffect(() => {
    if (searchParams?.get(tabName)) {
      setActiveTab(searchParams?.get(tabName));
    }
  }, [searchParams, tabName]);

  const handleTabChange = (key) => {
    if (key === activeTab) return;
    setActiveTab(key);
    setDataToSearchParams({ [tabName]: key });
    if (onChange) {
      onChange(key);
    }
  };
  // Variant styles
  const variants = {
    default: {
      container: 'border-b border-gray-200',
      innerRow: 'flex space-x-1 overflow-x-auto scrollbar-hide',
      button: (isActive) => `
        relative px-6 py-3 text-sm font-medium whitespace-nowrap transition-colors duration-200
        ${isActive ? 'text-blue-600' : 'text-gray-600 hover:text-gray-900'}
      `,
      indicator: 'absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-t-full',
    },
    pills: {
      container: 'bg-blue-100 p-1 rounded-lg',
      innerRow: 'flex space-x-1 overflow-x-auto scrollbar-hide',
      button: (isActive) => `
        px-6 py-2 text-sm font-medium whitespace-nowrap rounded-md transition-all duration-200
        ${
          isActive
            ? 'bg-white text-blue-600 shadow-sm'
            : 'text-gray-600 hover:text-gray-900 hover:text-blue-500'
        }
      `,
      indicator: null,
    },
    underline: {
      container: 'border-b border-gray-200',
      innerRow: 'flex space-x-8 overflow-x-auto scrollbar-hide',
      button: (isActive) => `
        relative pb-3 text-sm font-semibold whitespace-nowrap transition-colors duration-200
        ${isActive ? 'text-blue-600' : 'text-gray-500 hover:text-gray-800'}
      `,
      indicator: 'absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600',
    },
  };

  const currentVariant = variants[variant];

  return (
    <div className="">
      {/* Tab Headers */}
      <div className={currentVariant.container}>
        <div className={currentVariant.innerRow}>
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => handleTabChange(tab.key)}
              disabled={tab.disabled}
              className={`outline-none focus:outline-none focus:ring-0 focus-visible:outline-none ${currentVariant.button(
                activeTab === tab.key,
              )} ${tab.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {/* Tab Icon */}
              {tab.icon && <span className="inline-block mr-2 align-middle">{tab.icon}</span>}

              {/* Tab Label */}
              <span className="align-middle">{tab.label}</span>

              {/* Attention count (e.g. items waiting for the user) */}
              {tab.alertCount > 0 && (
                <span className="ml-2 inline-flex min-w-[20px] items-center justify-center rounded-full bg-red-100 px-1.5 py-0.5 align-middle text-[10px] font-semibold text-red-700">
                  {tab.alertCount}
                </span>
              )}
              {/* Active Indicator */}
              {activeTab === tab.key && currentVariant.indicator && (
                <span className={currentVariant.indicator}></span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content — NOTE: No CSS transforms here to preserve fixed-position stacking context for modals */}
      <div className="relative mt-4">
        {tabs.map((tab) => (
          <div
            key={tab.key}
            className={`transition-opacity duration-200 ${
              activeTab === tab.key
                ? 'opacity-100 relative'
                : 'opacity-0 absolute inset-0 pointer-events-none'
            }`}
          >
            {tab.content}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Tabs;
